import { useState, useEffect } from 'react';

type Language = 'fr' | 'en' | 'es' | 'de' | 'it';

// Mapping pays → langue
const COUNTRY_TO_LANGUAGE: Record<string, Language> = {
  // France et DOM-TOM
  FR: 'fr', RE: 'fr', GP: 'fr', MQ: 'fr', GF: 'fr', YT: 'fr', 
  NC: 'fr', PF: 'fr', PM: 'fr', WF: 'fr', TF: 'fr', BL: 'fr', 
  MF: 'fr', MC: 'fr',
  
  // Pays anglophones
  GB: 'en', US: 'en', CA: 'en', AU: 'en', NZ: 'en', IE: 'en',
  ZA: 'en', SG: 'en', IN: 'en', PK: 'en', NG: 'en', KE: 'en',
  GH: 'en', ZW: 'en', JM: 'en', TT: 'en', BS: 'en', BB: 'en',
  
  // Pays hispanophones
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', PE: 'es', VE: 'es',
  CL: 'es', EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es',
  HN: 'es', PY: 'es', SV: 'es', NI: 'es', CR: 'es', PA: 'es',
  UY: 'es', PR: 'es', GQ: 'es',
  
  // Pays germanophones
  DE: 'de', AT: 'de', CH: 'de', LI: 'de', LU: 'de',
  
  // Pays italophones
  IT: 'it', SM: 'it', VA: 'it',
};

export const useGeoLanguageDetection = () => {
  const [detectedLanguage, setDetectedLanguage] = useState<Language | null>(null);
  const [isDetecting, setIsDetecting] = useState(true);

  useEffect(() => {
    const detectLanguageFromIP = async () => {
      try {
        // Vérifier si une langue est déjà en cache
        const cachedLang = localStorage.getItem('geoLanguage');
        const cacheTimestamp = localStorage.getItem('geoLanguageTimestamp');
        
        // Cache valide pendant 7 jours
        if (cachedLang && cacheTimestamp) {
          const age = Date.now() - parseInt(cacheTimestamp);
          if (age < 7 * 24 * 60 * 60 * 1000) {
            setDetectedLanguage(cachedLang as Language);
            setIsDetecting(false);
            return;
          }
        }

        // Détection via ipapi.co
        const response = await fetch('https://ipapi.co/json/', {
          headers: { 'Accept': 'application/json' }
        });
        
        if (!response.ok) throw new Error('Geo detection failed');
        
        const data = await response.json();
        const countryCode = data.country_code as string;
        
        // Mapper le pays vers une langue supportée (fallback vers 'en')
        const language = COUNTRY_TO_LANGUAGE[countryCode] || 'en';
        
        // Mettre en cache
        localStorage.setItem('geoLanguage', language);
        localStorage.setItem('geoLanguageTimestamp', Date.now().toString());
        
        setDetectedLanguage(language);
        
      } catch (err) {
        console.warn('Geo language detection failed:', err);
        setDetectedLanguage('en'); // Fallback vers anglais
      } finally {
        setIsDetecting(false);
      }
    };

    detectLanguageFromIP();
  }, []);

  return { detectedLanguage, isDetecting };
};
